/* src/pages/admin/AdminPage.js */
import React, { useState, useEffect } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { 
    Menu as MenuIcon, 
    Close as CloseIcon,
    Dashboard as DashboardIcon,
    School as SchoolIcon,
    CastForEducation as LiveCourseIcon,
    Article as ArticleIcon,
    Lightbulb as RawThoughtsIcon,
    Videocam as VideoIcon,
    VideoLibrary as ReelIcon,
    Headphones as PodcastIcon,
    People as PeopleIcon,
    Comment as CommentIcon,
    Category as CategoryIcon,
    ExitToApp as ExitIcon,
    MenuBook as BookIcon,
    ExpandLess,
    ExpandMore,
    ChevronLeft,
    ChevronRight
} from '@mui/icons-material';
import './AdminPage.css';
import AdminBookManager from './AdminBookManager';

const AdminPage = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const location = useLocation();

    // Check if we're on mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Close sidebar when route changes on mobile
    // useEffect(() => {
    //     if (isMobile && isSidebarOpen) {
    //         setIsSidebarOpen(false);
    //     }
    // }, [location.pathname, isMobile, isSidebarOpen]);

    // Handle body scroll when sidebar is open
    useEffect(() => {
        if (isMobile && isSidebarOpen) {
            document.body.classList.add('sidebar-open');
        } else {
            document.body.classList.remove('sidebar-open');
        }

        // Cleanup on unmount
        return () => {
            document.body.classList.remove('sidebar-open');
        };
    }, [isMobile, isSidebarOpen]);

    // Debug sidebar state changes
    useEffect(() => {
        console.log('Sidebar state changed:', { isSidebarOpen, isMobile });
    }, [isSidebarOpen, isMobile]);

    const toggleSidebar = () => {
        console.log('Toggle sidebar clicked, current state:', isSidebarOpen);
        setIsSidebarOpen(!isSidebarOpen);
    };

    const closeSidebar = () => {
        console.log('Close sidebar called from:', new Error().stack);
        setIsSidebarOpen(false);
    };

    const handleSidebarClick = (e) => {
        // Prevent clicks inside sidebar from closing it
        e.stopPropagation();
    };

    const handleOverlayClick = () => {
        closeSidebar();
    };

    const handleNavClick = () => {
        // Only close sidebar on mobile when navigating
        if (isMobile) {
            setIsSidebarOpen(false);
        }
    };

    const groupedNavItems = [
        {
            heading: 'Content',
            items: [
                { path: '/admin/articles', icon: <ArticleIcon />, label: 'Articles' },
                { path: '/admin/raw-thoughts', icon: <RawThoughtsIcon />, label: 'Raw Thoughts' },
                { path: '/admin/videos', icon: <VideoIcon />, label: 'Videos' },
                { path: '/admin/reels', icon: <ReelIcon />, label: 'Reels' },
                { path: '/admin/books', icon: <BookIcon />, label: 'Books' },
                { path: '/admin/podcasts', icon: <PodcastIcon />, label: 'Podcasts' },
            ]
        },
        {
            heading: 'Courses',
            items: [
                { path: '/admin/courses', icon: <SchoolIcon />, label: 'Courses' },
                { path: '/admin/live-courses', icon: <LiveCourseIcon />, label: 'Live Courses' },
            ]
        },
        {
            heading: 'Users',
            items: [
                { path: '/admin/users', icon: <PeopleIcon />, label: 'Users' },
                { path: '/admin/comments', icon: <CommentIcon />, label: 'Comments' },
            ]
        },
        {
            heading: 'Other',
            items: [
                { path: '/admin/categories', icon: <CategoryIcon />, label: 'Categories' },
            ]
        }
    ];

    const [collapsedSections, setCollapsedSections] = useState({});
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    const toggleSection = (heading) => {
        setCollapsedSections(prev => ({ ...prev, [heading]: !prev[heading] }));
    };
    const toggleSidebarCollapse = () => {
        setSidebarCollapsed(val => !val);
    };

    return (
        <div className="admin-page-container">
            {/* Mobile Header */}
            <div className="admin-mobile-header">
                <button 
                    className="mobile-menu-btn" 
                    onClick={toggleSidebar}
                    aria-label="Toggle menu"
                >
                    <span className="material-icons">
                        {isSidebarOpen ? 'close' : 'menu'}
                    </span>
                </button>
                <h1 className="admin-mobile-title">Admin Dashboard</h1>
            </div>

            {/* Sidebar Overlay */}
            {isSidebarOpen && (
                <div 
                    className="sidebar-overlay" 
                    onClick={handleOverlayClick}
                />
            )}

            {/* Sidebar */}
            <div className={`admin-sidebar ${isSidebarOpen ? 'sidebar-open' : ''} ${isMobile ? 'mobile-sidebar' : ''} ${sidebarCollapsed && !isMobile ? 'collapsed' : ''}`}
                onClick={handleSidebarClick}
            >
                {!isMobile && (
                    <div className="admin-sidebar-header" style={{ display: 'flex', alignItems: 'center', justifyContent: sidebarCollapsed ? 'center' : 'space-between', marginBottom: 16 }}>
                        <h1 className="admin-logo">{sidebarCollapsed ? 'A' : 'Admin Panel'}</h1>
                        {!isMobile && (
                            <button className="sidebar-collapse-btn" onClick={toggleSidebarCollapse} aria-label="Collapse sidebar">
                                {sidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
                            </button>
                        )}
                    </div>
                )}
                {isMobile && (
                    <div className="admin-sidebar-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                        <h1 className="admin-logo">Admin Panel</h1>
                    </div>
                )}
                <nav className="admin-nav">
                    {groupedNavItems.map(section => (
                        <div className="sidebar-section" key={section.heading}>
                            {(!sidebarCollapsed || isMobile) && (
                                <div className="sidebar-heading" onClick={() => toggleSection(section.heading)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span>{section.heading}</span>
                                    {collapsedSections[section.heading] ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
                                </div>
                            )}
                            {!collapsedSections[section.heading] && (
                                <div className="sidebar-items">
                                    {section.items.map(item => (
                                        <NavLink
                                            key={item.path}
                                            to={item.path}
                                            className={({ isActive }) => isActive ? 'active' : ''}
                                            onClick={handleNavClick}
                                            style={sidebarCollapsed && !isMobile ? { justifyContent: 'center', position: 'relative' } : {}}
                                        >
                                            {item.icon}
                                            {(!sidebarCollapsed || isMobile) && <span className="nav-label">{item.label}</span>}
                                            {sidebarCollapsed && !isMobile && <span className="sidebar-tooltip">{item.label}</span>}
                                        </NavLink>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </nav>
                
                <div className="admin-sidebar-footer">
                    <Link to="/" className="sidebar-link" onClick={handleNavClick} style={sidebarCollapsed && !isMobile ? { justifyContent: 'center' } : {}}>
                        <ExitIcon />
                        {(!sidebarCollapsed || isMobile) && <span className="nav-label">Back to Site</span>}
                        {sidebarCollapsed && !isMobile && <span className="sidebar-tooltip">Back to Site</span>}
                    </Link>
                </div>
            </div>

            {/* Main Content */}
            <main className={`admin-main-content ${isMobile ? 'mobile-content' : ''}`}>
                <Outlet />
            </main>
        </div>
    );
};

export default AdminPage;
